import {
  Provider as SupabaseProvider,
  Session,
  UserMetadata,
} from "@supabase/supabase-js";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { supabase } from "../config/supabase";
import { clearOutlineContainerHints } from "../data/mindmapExpand";
import {
  invalidateNotionPagesCache,
  prefetchNotionPages,
} from "../data/notionPagesCache";
import {
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from "../helper/localStorage";
import { clearNotionRequestCache } from "../services/notion";

interface AuthContext {
  user: UserMetadata;
  session?: Session;
  /** false until the initial session lookup settles (guards a 404 flash). */
  authResolved: boolean;
  feedbackSubmitted: boolean | null; // null = loading, false = show form, true = hide form
  /**
   * `returnHere` comes back to the current URL after login (a gated page such
   * as `/agents`); the default is the origin, i.e. the canvas.
   */
  signIn: (
    provider: SupabaseProvider,
    options?: { returnHere?: boolean },
  ) => Promise<void>;
  signOut: () => Promise<void>;
  markFeedbackSubmitted: () => void;
}

const authContext = createContext({ user: {} } as AuthContext);
const { Provider } = authContext;

// AuthProvider is a Context Provider that wraps our app and makes an auth object
// available to any child component that calls the useAuth() hook.
export function AuthProvider(props: { children: ReactNode }): JSX.Element {
  const auth = useAuthProvider();
  return <Provider value={auth}>{props.children}</Provider>;
}

// useAuth is a hook that enables any component to subscribe to auth state
export const useAuth = () => {
  return useContext(authContext);
};

// Provider hook that creates auth object and handles state
const useAuthProvider = () => {
  const [user, setUser] = useState({});
  const [authResolved, setAuthResolved] = useState(false);
  const [session, setSession]: [
    Session | undefined,
    Dispatch<SetStateAction<Session | undefined>>,
  ] = useState();

  // Initialize feedbackSubmitted from localStorage to prevent flash
  // Returns: true = submitted (cached), null = needs verification
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<boolean | null>(
    () => {
      const cached = getLocalStorage("mindmap-feedback-submitted");
      return cached === "true" ? true : null;
    },
  );

  const signIn = async (
    provider: SupabaseProvider,
    options?: { returnHere?: boolean },
  ) => {
    try {
      setLocalStorage("isUserLoggedIn", "false");

      const result = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: options?.returnHere
            ? window.location.href
            : window.location.origin,
        },
      });

      if (result.error) {
        console.error("OAuth error:", result.error);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Sign-in error:", error);
    }
  };

  const signOut = async () => {
    removeLocalStorage(["isUserLoggedIn", "mindmap-feedback-submitted"]);
    // Every Notion cache is keyed by Notion id alone, with no user in the key,
    // and all of them outlive a sign-out in module scope. On a shared machine
    // the next account would be served the previous one's Notion content —
    // including pages it cannot read — for the rest of the TTL. The workspace
    // page list is the worst of the three: it is the whole picker.
    invalidateNotionPagesCache();
    clearNotionRequestCache();
    clearOutlineContainerHints();
    await supabase.auth.signOut();
  };

  const markFeedbackSubmitted = () => {
    setLocalStorage("mindmap-feedback-submitted", "true");
    setFeedbackSubmitted(true);
  };

  useEffect(() => {
    supabase.auth
      .getSession()
      .then((response) => {
        if (response.data.session) {
          setSession(response.data.session);
        }
      })
      .finally(() => setAuthResolved(true));
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      session ? setSession(session) : setSession(undefined);
      setAuthResolved(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  const previousUserIdRef = useRef<string | undefined>(undefined);
  // Warm the picker's workspace list well before it is opened, so the first
  // open paints a list instead of a spinner. Gated on `isUserLoggedIn` because
  // the edge function reads the stored provider token, which the upsert below
  // is what puts there — on a first sign-in the prefetch runs from there.
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = session?.user?.id;

    if (!session?.user?.id) {
      invalidateNotionPagesCache();
      return;
    }

    // A → B WITHOUT passing through null. Invalidating only on the absent
    // session missed it: the effect saw B's id, the prefetch no-opped on an
    // already-loaded cache, and B browsed A's pages.
    if (previousUserId && previousUserId !== session.user.id) {
      invalidateNotionPagesCache();
    }

    if (getLocalStorage("isUserLoggedIn") === "true") {
      void prefetchNotionPages();
    }
  }, [session?.user?.id]);

  useEffect(() => {
    if (session?.user?.user_metadata) {
      setUser(session.user.user_metadata);
    }

    const isUserLoggedIn = getLocalStorage("isUserLoggedIn");
    //temp fix need to find permenant solution
    if (
      session?.provider_token &&
      session?.user?.id &&
      isUserLoggedIn === "false"
    ) {
      supabase
        .from("provider_tokens")
        .upsert(
          {
            user_id: session.user.id,
            provider: "notion",
            provider_token: session.provider_token,
          },
          {
            onConflict: "user_id",
          },
        )
        .then(
          (res) => {
            if (res.error) {
              console.error("Failed to upsert provider token:", res.error);
              return;
            }
            if (res.status === 200 || res.status === 201) {
              setLocalStorage("isUserLoggedIn", "true");
              void prefetchNotionPages();
            }
          },
          (error) => {
            // Network-level rejection (the request never resolved).
            console.error("Error upserting provider token:", error);
          },
        );
    }

    // Check feedback status after authentication
    const checkFeedbackStatus = async () => {
      if (!session?.user?.id) {
        return;
      }

      // If already cached as submitted, skip DB check
      if (feedbackSubmitted === true) {
        return;
      }

      // Check database to verify if user has submitted feedback
      try {
        const { data, error } = await supabase
          .from("user_feedback")
          .select("id")
          .eq("user_id", session.user.id)
          .maybeSingle();

        if (error) {
          console.error("Error checking feedback status:", error);
          setFeedbackSubmitted(false);
          return;
        }

        if (data) {
          // User has submitted feedback, cache it
          setLocalStorage("mindmap-feedback-submitted", "true");
          setFeedbackSubmitted(true);
        } else {
          // User hasn't submitted feedback yet
          setFeedbackSubmitted(false);
        }
      } catch (error) {
        console.error("Error checking feedback status:", error);
        setFeedbackSubmitted(false);
      }
    };

    // Check immediately without delay to prevent flash
    if (session?.user?.id) {
      checkFeedbackStatus();
    }
  }, [session?.provider_token, session?.user, feedbackSubmitted]);

  return {
    user,
    session,
    authResolved,
    feedbackSubmitted,
    signIn,
    signOut,
    markFeedbackSubmitted,
  };
};
